import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  IconButton,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import API from 'util/API';

const ORDER_TYPES = [
  { value: 'dinein', label: 'Dine-in' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'takeaway', label: 'Takeaway' },
  { value: 'pickup', label: 'Pickup' },
  { value: 'other', label: 'Other' },
];

const PAYMENT_OPTIONS = [
  { value: 'required', label: 'Required' },
  { value: 'not_required', label: 'Not Required' },
  { value: 'optional', label: 'Optional' },
];

const POS_ACCEPT_OPTIONS = [
  { value: 'automatic', label: 'Automatically' },
  { value: 'manual', label: 'Manual' },
];

const QrCodeSettingsDialog = ({ 
  open, 
  onClose, 
  qrCodeSettings,
  onSettingsUpdate,
  showSuccess,
  showError,
  uiLoading,
  setUiLoading
}) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [settingToDelete, setSettingToDelete] = useState(null);

  const handleDeleteClick = (setting) => {
    setSettingToDelete(setting);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!settingToDelete?.shortUrlCode) {
      showError('No short URL code found');
      setDeleteConfirmOpen(false);
      return;
    }

    setUiLoading(true);
    try {
      await API.delete(`/deleteRestaurantOrderShortUrl/${settingToDelete.shortUrlCode}`);
      
      // Update parent component's state
      const updatedSettings = qrCodeSettings.map(s => {
        if (s.orderType === settingToDelete.orderType || s.name === settingToDelete.name) {
          return { ...s, shortUrl: '', shortUrlCode: '' };
        }
        return s;
      });
      onSettingsUpdate(updatedSettings);
      
      showSuccess('Short URL deleted successfully');
    } catch (error) {
      console.error('Error deleting short URL:', error);
      showError(error.response?.data?.message || 'Failed to delete short URL');
    } finally {
      setUiLoading(false);
      setDeleteConfirmOpen(false);
      setSettingToDelete(null);
    }
  };

  return (
    <>
      {/* QR Code Settings Dialog */}
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>All QR Code Settings</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Order Type</strong></TableCell>
                    <TableCell><strong>Payment</strong></TableCell>
                    <TableCell><strong>POS Accept</strong></TableCell>
                    <TableCell><strong>Short URL</strong></TableCell>
                    <TableCell><strong>Expires</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {qrCodeSettings.length > 0 ? (
                    qrCodeSettings.map((setting) => (
                      <TableRow key={setting.orderType || setting.name}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {ORDER_TYPES.find(o => o.value === (setting.orderType || setting.name))?.label || ''}
                            </Typography>
                            {setting.name && setting.name !== setting.orderType && (
                              <Typography variant="caption" color="textSecondary">
                                ({setting.name})
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {PAYMENT_OPTIONS.find(o => o.value === setting.payment)?.label || ''}
                        </TableCell>
                        <TableCell>
                          {POS_ACCEPT_OPTIONS.find(o => o.value === setting.posAcceptOrders)?.label || ''}
                        </TableCell>
                        <TableCell>
                          {setting.shortUrl ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2" noWrap style={{ maxWidth: 200 }}>
                                {setting.shortUrl}
                              </Typography>
                              <IconButton 
                                size="small" 
                                onClick={() => {
                                  navigator.clipboard.writeText(setting.shortUrl);
                                  showSuccess('Short URL copied!');
                                }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              Not generated
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {setting.expiresTimestamp ? (
                            <Typography variant="body2">
                              {new Date(parseInt(setting.expiresTimestamp)).toLocaleString()}
                            </Typography>
                          ) : (
                            <Chip 
                              label="Never" 
                              color="default"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={setting.disabled ? 'Disabled' : 'Active'} 
                            color={setting.disabled ? 'default' : 'success'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {setting.shortUrl && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(setting)}
                              disabled={uiLoading}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No QR code settings configured
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Delete Short URL</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete the short URL for{' '}
            <strong>
              {ORDER_TYPES.find(o => o.value === (settingToDelete?.orderType || settingToDelete?.name))?.label || settingToDelete?.name}
            </strong>
            ?
          </Typography>
          <Typography variant="body2" color="error" style={{ marginTop: 16 }}>
            This may impact online ordering.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={uiLoading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QrCodeSettingsDialog;
